import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Chip,
  Collapse,
  Divider,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  AttachMoney as CostIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Refresh as RefreshIcon,
  AccessTime as TimerIcon,
} from '@mui/icons-material';
import albumService from '../../api/albumService';

/**
 * Format milliseconds into a human-readable duration string (e.g. "2m 3s" or "45.2s").
 */
function formatDuration(ms) {
  if (!ms || ms <= 0) return null;
  if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
  const mins = Math.floor(ms / 60000);
  const secs = Math.round((ms % 60000) / 1000);
  return `${mins}m ${secs}s`;
}

function formatBytes(bytes) {
  if (!bytes || bytes <= 0) return null;
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Compact collapsible panel showing the session-level AI cost summary
 * (aggregated across Steps 2, 3, 5 and 6).
 */
const STEP_LABELS = {
  upload:          'Step 1 – Image Upload',
  faceRecognition: 'Step 2 – Face Recognition',
  embeddings:      'Step 3 – Analysis & Embedding',
  storyTimeline:   'Step 5 – Story Timeline',
  albumPlan:       'Step 6a – Album Plan',
  albumLayout:     'Step 6 – Album Layout',
};

// Distinct color per step (MUI palette color names)
const STEP_COLORS = {
  upload:          '#1565c0', // blue-900
  faceRecognition: '#6a1b9a', // purple-900
  embeddings:      '#00695c', // teal-800
  storyTimeline:   '#e65100', // deep-orange-900
  albumPlan:       '#2e7d32', // green-900
  albumLayout:     '#558b2f', // light-green-800
};

const AiCostSummaryPanel = ({ sessionId, refreshKey = 0 }) => {
  const [summary, setSummary] = useState(null);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async (forceRefresh = false) => {
    if (!sessionId) return;
    setLoading(true);
    try {
      const data = await albumService.getSessionAiCostSummary(sessionId, forceRefresh);
      setSummary(data?.aiCostSummary || null);
    } catch (err) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Failed to load AI cost summary:', err);
      }
    } finally {
      setLoading(false);
    }
  }, [sessionId]);

  // Load on mount; re-fetch whenever refreshKey bumps (e.g. after Step 3 completes)
  useEffect(() => { load(refreshKey > 0); }, [load, refreshKey]);

  if (!summary && !loading) return null;

  const totalCost = summary?.totalCostUsd ?? 0;
  const totalDuration = formatDuration(summary?.totalDurationMs);
  const stepEntries = Object.entries(summary?.steps || {});

  return (
    <Paper variant="outlined" sx={{ mb: 2, px: 2, py: 1 }}>
      <Stack direction="row" alignItems="center" spacing={1}>
        <CostIcon fontSize="small" color="action" />
        <Typography variant="caption" color="text.secondary" sx={{ flex: 1, fontWeight: 600 }}>
          AI Cost Summary {summary?.hasEstimates ? '(includes estimates)' : '(actual)'}
        </Typography>

        {totalCost > 0 && (
          <Chip
            label={`$${totalCost.toFixed(4)} total`}
            size="small"
            color="secondary"
            variant="filled"
          />
        )}

        {totalDuration && (
          <Chip
            icon={<TimerIcon sx={{ fontSize: '12px !important' }} />}
            label={totalDuration}
            size="small"
            variant="outlined"
            sx={{ fontSize: 10 }}
            title="Total AI processing time"
          />
        )}

        <Tooltip title="Refresh summary">
          <IconButton size="small" onClick={() => load(true)} disabled={loading}>
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Tooltip>

        <IconButton size="small" onClick={() => setOpen(o => !o)}>
          {open ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
        </IconButton>
      </Stack>

      <Collapse in={open}>
        <Divider sx={{ my: 1 }} />
        <Stack spacing={0.75}>
          {stepEntries.map(([key, step]) => {
            const costUsd = step.costUsd ?? step.estimatedCostUsd ?? 0;
            // Human-readable provider label
            const providerLabel = step.provider === 'aws_rekognition' ? 'AWS Rekognition'
              : step.provider === 'openai' ? null  // model chip already shows this
              : step.provider || null;
            return (
              <Box key={key} sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                {/* Colored step number badge */}
                <Box
                  sx={{
                    minWidth: 22, height: 22,
                    borderRadius: '50%',
                    bgcolor: STEP_COLORS[key] || 'grey.600',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <Typography variant="caption" sx={{ color: '#fff', fontWeight: 700, fontSize: 10, lineHeight: 1 }}>
                    {step.step}
                  </Typography>
                </Box>
                <Typography variant="caption" sx={{ minWidth: 200, color: STEP_COLORS[key] || 'text.secondary', fontWeight: 600 }}>
                  {STEP_LABELS[key] || key}
                </Typography>

                {/* Provider chip for non-OpenAI steps (Step 2) */}
                {providerLabel && (
                  <Chip label={providerLabel} size="small" variant="outlined" sx={{ fontSize: 10 }} />
                )}

                {/* Provider / model */}
                {step.visionModel && (
                  <Chip label={step.visionModel} size="small" variant="outlined" sx={{ fontSize: 10 }} />
                )}
                {step.model && (
                  <Chip label={step.model} size="small" variant="outlined" sx={{ fontSize: 10 }} />
                )}

                {/* Token count */}
                {step.totalTokens > 0 && (
                  <Chip
                    label={`${step.totalTokens.toLocaleString()} tokens`}
                    size="small"
                    color="info"
                    variant="outlined"
                    sx={{ fontSize: 10 }}
                  />
                )}
                {step.imagesProcessed > 0 && !step.totalTokens && (
                  <Chip
                    label={`${step.imagesProcessed} images`}
                    size="small"
                    variant="outlined"
                    sx={{ fontSize: 10 }}
                  />
                )}
                {/* Total upload size (Step 1 only) */}
                {step.totalSizeBytes > 0 && (
                  <Chip
                    label={formatBytes(step.totalSizeBytes)}
                    size="small"
                    variant="outlined"
                    sx={{ fontSize: 10 }}
                    title="Total uploaded size"
                  />
                )}
                {/* Matches found (Step 2 only) */}
                {step.matchesFound != null && step.matchesFound > 0 && (
                  <Chip
                    label={`${step.matchesFound} matches`}
                    size="small"
                    color="success"
                    variant="outlined"
                    sx={{ fontSize: 10 }}
                    title="Persons recognised"
                  />
                )}

                {/* Cost */}
                {costUsd > 0 && (
                  <Chip
                    label={`${step.isEstimated ? '~' : ''}$${costUsd.toFixed(4)}`}
                    size="small"
                    color="secondary"
                    variant="outlined"
                    sx={{ fontSize: 10 }}
                    title={step.isEstimated ? 'Estimated cost' : 'Actual cost'}
                  />
                )}

                {/* Duration */}
                {step.durationMs > 0 && (
                  <Chip
                    icon={<TimerIcon sx={{ fontSize: '12px !important' }} />}
                    label={formatDuration(step.durationMs)}
                    size="small"
                    variant="outlined"
                    sx={{ fontSize: 10 }}
                    title="Elapsed processing time"
                  />
                )}
              </Box>
            );
          })}

          {stepEntries.length === 0 && (
            <Typography variant="caption" color="text.secondary">
              No AI steps completed yet.
            </Typography>
          )}

          {summary?.totalTokens > 0 && (
            <>
              <Divider sx={{ my: 0.5 }} />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="caption" sx={{ minWidth: 220, fontWeight: 600 }}>
                  Total
                </Typography>
                <Chip
                  label={`${summary.totalTokens.toLocaleString()} tokens`}
                  size="small"
                  color="info"
                  sx={{ fontSize: 10 }}
                />
                <Chip
                  label={`${summary.hasEstimates ? '~' : ''}$${totalCost.toFixed(4)}`}
                  size="small"
                  color="secondary"
                  sx={{ fontSize: 10 }}
                />
                {totalDuration && (
                  <Chip
                    icon={<TimerIcon sx={{ fontSize: '12px !important' }} />}
                    label={totalDuration}
                    size="small"
                    color="default"
                    sx={{ fontSize: 10 }}
                    title="Total AI processing time"
                  />
                )}
              </Box>
            </>
          )}
        </Stack>
      </Collapse>
    </Paper>
  );
};

export default AiCostSummaryPanel;
